import React, { useState } from 'react';
import { Settings as SettingsIcon } from 'lucide-react';
import { PageHeader } from '../../components/shared/PageHeader';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { JiraSettings } from './components/JiraSettings';
import { GithubSettings } from './components/GithubSettings';
import { QuickBooksSettings } from './components/QuickBooksSettings';
import { GeneralSettings } from './components/GeneralSettings';
import { ThemeCustomization } from './components/ThemeCustomization';
import { useAuth } from '../../core/contexts/AuthContext';
import { UserRole } from '../../core/models/User';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';

export const SettingsPage: React.FC = () => {
  const { currentUser, organizationId } = useAuth();
  const { organizations } = useOrganizations();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(organizationId);

  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const effectiveOrgId = isSuperAdmin ? selectedOrgId : organizationId;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Manage integrations and application settings"
        icon={SettingsIcon}
      />

      <Tabs defaultValue="general" className="w-full">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="theme">Theme</TabsTrigger>
          <TabsTrigger value="jira">Jira Integration</TabsTrigger>
          <TabsTrigger value="github">GitHub Integration</TabsTrigger>
          <TabsTrigger value="quickbooks">QuickBooks</TabsTrigger>
        </TabsList>

        {/* Jira Tab */}
        <TabsContent value="jira">
          <JiraSettings />
        </TabsContent>

        {/* GitHub Tab */}
        <TabsContent value="github">
          <GithubSettings />
        </TabsContent>

        {/* QuickBooks Tab */}
        <TabsContent value="quickbooks">
          <QuickBooksSettings />
        </TabsContent>

        {/* General Tab */}
        <TabsContent value="general">
          <div className="space-y-4">
            {isSuperAdmin && (
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">Client Context</p>
                <p className="text-xs text-muted-foreground">
                  As a super admin, select which client&apos;s settings and branding you want to manage.
                </p>
                <Select
                  value={effectiveOrgId || ''}
                  onValueChange={(value) => setSelectedOrgId(value || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {effectiveOrgId ? (
              <GeneralSettings organizationIdOverride={effectiveOrgId} />
            ) : (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                {isSuperAdmin
                  ? 'Select a client above to edit its settings and branding.'
                  : 'No client is associated with your user. Contact an administrator to be assigned to a client.'}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Theme Tab */}
        <TabsContent value="theme">
          <ThemeCustomization />
        </TabsContent>
      </Tabs>
    </div>
  );
};
